/**
 * Sancta locale — 12 pre-translated UI strings for the chatbot widget.
 *
 * Each entry mirrors the same shape so the widget always renders. If we
 * ever add a 13th language we just append here, no code changes elsewhere.
 * The ACTUAL AI conversation always flows through Claude Sonnet 4.6 which
 * handles every world language natively — this file only localizes the
 * static UI (greeting, placeholder, starter labels, "Sancta joined").
 *
 * Persistence: user's pick is stored in localStorage under `sb_sancta_lang`.
 */
import { useEffect, useState, useCallback } from "react";

export interface SanctaLocaleStrings {
  greeting_line1: string;                  // "Hey <name>! I'm Sancta — ..."
  greeting_line2: string;                  // "What can I help with?"
  input_placeholder: string;               // "Message Sancta..."
  joined_line: string;                     // "Sancta joined the conversation"
  active_label: string;                    // "Active"
  send_aria: string;                       // "Send"
  suggestions_header: string;              // "Suggestions"
  thinking: string;                        // "thinking..."
  starter_sibascore: string;               // "What is SibaScore?"
  starter_onboard_brand: string;           // "How do I list my brand?"
  starter_aicart: string;                  // "How does the AI Cart work?"
  starter_pricing: string;                 // "Which plan fits me best?"
  voice_reply_on: string;                  // Auto-speak toggle label (ON)
  voice_reply_off: string;                 // Auto-speak toggle label (OFF)
  voice_reply_hint: string;                // Sub-label for the toggle row
}

export interface SanctaLanguage {
  code: string;    // ISO 639-1
  name: string;    // English display name for the option list
  nativeName: string;  // native display name (Hindi rendered in Devanagari, etc.)
  strings: SanctaLocaleStrings;
}

export const SANCTA_LANGUAGES: SanctaLanguage[] = [
  {
    code: "en", name: "English", nativeName: "English",
    strings: {
      greeting_line1: "Hey {name}! I'm Sancta — I explain any part of SociBaba, help you onboard, or answer product questions in your language.",
      greeting_line2: "What can I help with?",
      input_placeholder: "Message Sancta...",
      joined_line: "Sancta joined the conversation",
      active_label: "Active",
      send_aria: "Send",
      suggestions_header: "Suggestions",
      thinking: "thinking...",
      starter_sibascore: "What is SibaScore?",
      starter_onboard_brand: "How do I list my brand?",
      starter_aicart: "How does AI Cart™ work?",
      starter_pricing: "Which plan fits me best?",
      voice_reply_on: "Speak replies",
      voice_reply_off: "Silent replies",
      voice_reply_hint: "Hear Sancta read every answer aloud.",
    },
  },
  {
    code: "hi", name: "Hindi", nativeName: "हिन्दी",
    strings: {
      greeting_line1: "नमस्ते {name}! मैं Sancta हूँ — SociBaba का हर हिस्सा समझाऊँ, onboarding में मदद करूँ, या आपकी भाषा में सवालों के जवाब दूँ।",
      greeting_line2: "कैसे मदद करूँ?",
      input_placeholder: "Sancta को संदेश भेजें…",
      joined_line: "Sancta बातचीत में शामिल हुई",
      active_label: "सक्रिय",
      send_aria: "भेजें",
      suggestions_header: "सुझाव",
      thinking: "सोच रही हूँ…",
      starter_sibascore: "SibaScore क्या है?",
      starter_onboard_brand: "मैं अपना ब्रांड कैसे लिस्ट करूँ?",
      starter_aicart: "AI Cart™ कैसे काम करता है?",
      starter_pricing: "मेरे लिए कौन-सा प्लान सही है?",
      voice_reply_on: "जवाब सुनाओ",
      voice_reply_off: "आवाज़ बंद",
      voice_reply_hint: "Sancta हर जवाब आपकी भाषा में बोलेगी।",
    },
  },
  {
    code: "ta", name: "Tamil", nativeName: "தமிழ்",
    strings: {
      greeting_line1: "வணக்கம் {name}! நான் Sancta — SociBaba-வின் ஒவ்வொரு அம்சத்தையும் விளக்குகிறேன், onboarding-ல் உதவுகிறேன், உங்கள் மொழியில் பதிலளிக்கிறேன்.",
      greeting_line2: "எப்படி உதவட்டும்?",
      input_placeholder: "Sancta-க்கு செய்தி…",
      joined_line: "Sancta உரையாடலில் சேர்ந்தார்",
      active_label: "செயலில்",
      send_aria: "அனுப்பு",
      suggestions_header: "பரிந்துரைகள்",
      thinking: "யோசிக்கிறேன்…",
      starter_sibascore: "SibaScore என்றால் என்ன?",
      starter_onboard_brand: "என் பிராண்டை எப்படி பட்டியலிடுவது?",
      starter_aicart: "AI Cart™ எப்படி வேலை செய்கிறது?",
      starter_pricing: "எனக்கு எந்த திட்டம் பொருந்தும்?",
      voice_reply_on: "பதில் ஒலிக்க",
      voice_reply_off: "ஒலி இல்லை",
      voice_reply_hint: "Sancta ஒவ்வொரு பதிலையும் உரக்க வாசிக்கும்.",
    },
  },
  {
    code: "te", name: "Telugu", nativeName: "తెలుగు",
    strings: {
      greeting_line1: "హాయ్ {name}! నేను Sancta — SociBaba యొక్క ప్రతి భాగాన్ని వివరిస్తాను, onboarding-లో సహాయపడతాను, మీ భాషలో సమాధానం ఇస్తాను.",
      greeting_line2: "ఎలా సహాయం చేయను?",
      input_placeholder: "Sancta కి సందేశం…",
      joined_line: "Sancta సంభాషణలో చేరింది",
      active_label: "క్రియాశీలం",
      send_aria: "పంపు",
      suggestions_header: "సూచనలు",
      thinking: "ఆలోచిస్తున్నాను…",
      starter_sibascore: "SibaScore అంటే ఏమిటి?",
      starter_onboard_brand: "నా బ్రాండ్‌ని ఎలా జాబితా చేయాలి?",
      starter_aicart: "AI Cart™ ఎలా పని చేస్తుంది?",
      starter_pricing: "నాకు ఏ ప్లాన్ సరిపోతుంది?",
      voice_reply_on: "సమాధానం చదవండి",
      voice_reply_off: "నిశ్శబ్దం",
      voice_reply_hint: "Sancta ప్రతి సమాధానాన్ని బిగ్గరగా చదువుతుంది.",
    },
  },
  {
    code: "kn", name: "Kannada", nativeName: "ಕನ್ನಡ",
    strings: {
      greeting_line1: "ನಮಸ್ಕಾರ {name}! ನಾನು Sancta — SociBaba-ದ ಪ್ರತಿಯೊಂದು ಭಾಗವನ್ನೂ ವಿವರಿಸುತ್ತೇನೆ, onboarding-ನಲ್ಲಿ ಸಹಾಯ ಮಾಡುತ್ತೇನೆ, ನಿಮ್ಮ ಭಾಷೆಯಲ್ಲಿ ಉತ್ತರಿಸುತ್ತೇನೆ.",
      greeting_line2: "ಹೇಗೆ ಸಹಾಯ ಮಾಡಲಿ?",
      input_placeholder: "Sancta ಗೆ ಸಂದೇಶ…",
      joined_line: "Sancta ಸಂಭಾಷಣೆಗೆ ಸೇರಿಕೊಂಡರು",
      active_label: "ಸಕ್ರಿಯ",
      send_aria: "ಕಳುಹಿಸಿ",
      suggestions_header: "ಸಲಹೆಗಳು",
      thinking: "ಆಲೋಚಿಸುತ್ತಿದ್ದೇನೆ…",
      starter_sibascore: "SibaScore ಎಂದರೇನು?",
      starter_onboard_brand: "ನನ್ನ ಬ್ರಾಂಡ್ ಅನ್ನು ಹೇಗೆ ಪಟ್ಟಿ ಮಾಡುವುದು?",
      starter_aicart: "AI Cart™ ಹೇಗೆ ಕೆಲಸ ಮಾಡುತ್ತದೆ?",
      starter_pricing: "ನನಗೆ ಯಾವ ಪ್ಲಾನ್ ಸೂಕ್ತ?",
      voice_reply_on: "ಜೋರಾಗಿ ಓದಿ",
      voice_reply_off: "ಶಾಂತವಾಗಿ",
      voice_reply_hint: "Sancta ಪ್ರತಿ ಉತ್ತರವನ್ನೂ ಜೋರಾಗಿ ಓದುತ್ತದೆ.",
    },
  },
  {
    code: "ml", name: "Malayalam", nativeName: "മലയാളം",
    strings: {
      greeting_line1: "ഹായ് {name}! ഞാൻ Sancta — SociBaba-യുടെ ഏത് ഭാഗവും വിശദീകരിക്കാം, onboarding-ൽ സഹായിക്കാം, നിങ്ങളുടെ ഭാഷയിൽ മറുപടി പറയാം.",
      greeting_line2: "എങ്ങനെ സഹായിക്കാം?",
      input_placeholder: "Sancta-യ്ക്ക് സന്ദേശം…",
      joined_line: "Sancta സംഭാഷണത്തിൽ ചേർന്നു",
      active_label: "സജീവം",
      send_aria: "അയക്കുക",
      suggestions_header: "നിർദ്ദേശങ്ങൾ",
      thinking: "ചിന്തിക്കുന്നു…",
      starter_sibascore: "SibaScore എന്താണ്?",
      starter_onboard_brand: "എന്റെ ബ്രാൻഡ് എങ്ങനെ ലിസ്റ്റ് ചെയ്യാം?",
      starter_aicart: "AI Cart™ എങ്ങനെ പ്രവർത്തിക്കുന്നു?",
      starter_pricing: "എനിക്ക് ഏത് പ്ലാൻ അനുയോജ്യമാണ്?",
      voice_reply_on: "ഉച്ചത്തിൽ വായിക്കുക",
      voice_reply_off: "നിശ്ശബ്ദം",
      voice_reply_hint: "Sancta എല്ലാ മറുപടിയും ഉച്ചത്തിൽ വായിക്കും.",
    },
  },
  {
    code: "bn", name: "Bengali", nativeName: "বাংলা",
    strings: {
      greeting_line1: "হ্যালো {name}! আমি Sancta — SociBaba-এর যেকোনো অংশ ব্যাখ্যা করি, onboarding-এ সাহায্য করি, আপনার ভাষায় উত্তর দিই।",
      greeting_line2: "কীভাবে সাহায্য করতে পারি?",
      input_placeholder: "Sancta কে বার্তা…",
      joined_line: "Sancta কথোপকথনে যোগ দিয়েছেন",
      active_label: "সক্রিয়",
      send_aria: "পাঠান",
      suggestions_header: "পরামর্শ",
      thinking: "ভাবছি…",
      starter_sibascore: "SibaScore কী?",
      starter_onboard_brand: "আমি কীভাবে আমার ব্র্যান্ড তালিকাভুক্ত করব?",
      starter_aicart: "AI Cart™ কীভাবে কাজ করে?",
      starter_pricing: "আমার জন্য কোন প্ল্যান উপযুক্ত?",
      voice_reply_on: "উত্তর শোনাও",
      voice_reply_off: "নীরব উত্তর",
      voice_reply_hint: "Sancta প্রতিটি উত্তর জোরে পড়বে।",
    },
  },
  {
    code: "mr", name: "Marathi", nativeName: "मराठी",
    strings: {
      greeting_line1: "नमस्कार {name}! मी Sancta — SociBaba चा प्रत्येक भाग समजावतो, onboarding मध्ये मदत करतो, तुमच्या भाषेत उत्तर देतो.",
      greeting_line2: "मी कशी मदत करू?",
      input_placeholder: "Sancta ला संदेश…",
      joined_line: "Sancta संभाषणात सामील झाली",
      active_label: "सक्रिय",
      send_aria: "पाठवा",
      suggestions_header: "सूचना",
      thinking: "विचार करत आहे…",
      starter_sibascore: "SibaScore म्हणजे काय?",
      starter_onboard_brand: "मी माझा ब्रँड कसा सूचीबद्ध करू?",
      starter_aicart: "AI Cart™ कसे कार्य करते?",
      starter_pricing: "माझ्यासाठी कोणती योजना योग्य आहे?",
      voice_reply_on: "उत्तर वाचा",
      voice_reply_off: "शांत उत्तर",
      voice_reply_hint: "Sancta प्रत्येक उत्तर मोठ्याने वाचेल.",
    },
  },
  {
    code: "gu", name: "Gujarati", nativeName: "ગુજરાતી",
    strings: {
      greeting_line1: "નમસ્તે {name}! હું Sancta — SociBaba નો દરેક ભાગ સમજાવું, onboarding માં મદદ કરું, તમારી ભાષામાં જવાબ આપું.",
      greeting_line2: "કેવી રીતે મદદ કરું?",
      input_placeholder: "Sancta ને સંદેશ…",
      joined_line: "Sancta વાર્તાલાપમાં જોડાયા",
      active_label: "સક્રિય",
      send_aria: "મોકલો",
      suggestions_header: "સૂચનો",
      thinking: "વિચારી રહી છું…",
      starter_sibascore: "SibaScore શું છે?",
      starter_onboard_brand: "હું મારો બ્રાન્ડ કેવી રીતે લિસ્ટ કરું?",
      starter_aicart: "AI Cart™ કેવી રીતે કામ કરે છે?",
      starter_pricing: "મારા માટે કઈ પ્લાન યોગ્ય છે?",
      voice_reply_on: "જવાબ સાંભળો",
      voice_reply_off: "શાંત જવાબ",
      voice_reply_hint: "Sancta દરેક જવાબ મોટેથી વાંચશે.",
    },
  },
  {
    code: "es", name: "Spanish", nativeName: "Español",
    strings: {
      greeting_line1: "¡Hola {name}! Soy Sancta — te explico cualquier parte de SociBaba, te ayudo con el onboarding y respondo en tu idioma.",
      greeting_line2: "¿En qué te ayudo?",
      input_placeholder: "Mensaje a Sancta…",
      joined_line: "Sancta se unió a la conversación",
      active_label: "Activa",
      send_aria: "Enviar",
      suggestions_header: "Sugerencias",
      thinking: "pensando…",
      starter_sibascore: "¿Qué es SibaScore?",
      starter_onboard_brand: "¿Cómo publico mi marca?",
      starter_aicart: "¿Cómo funciona AI Cart™?",
      starter_pricing: "¿Qué plan me conviene?",
      voice_reply_on: "Leer respuestas",
      voice_reply_off: "Sin voz",
      voice_reply_hint: "Sancta leerá cada respuesta en voz alta.",
    },
  },
  {
    code: "ar", name: "Arabic", nativeName: "العربية",
    strings: {
      greeting_line1: "مرحبًا {name}! أنا Sancta — أشرح لك أي جزء من SociBaba، وأساعدك في التسجيل، وأجيبك بلغتك.",
      greeting_line2: "كيف يمكنني مساعدتك؟",
      input_placeholder: "رسالة إلى Sancta…",
      joined_line: "انضمت Sancta إلى المحادثة",
      active_label: "متصلة",
      send_aria: "إرسال",
      suggestions_header: "اقتراحات",
      thinking: "أفكر…",
      starter_sibascore: "ما هو SibaScore؟",
      starter_onboard_brand: "كيف أدرج علامتي التجارية؟",
      starter_aicart: "كيف يعمل AI Cart™؟",
      starter_pricing: "أي خطة تناسبني؟",
      voice_reply_on: "قراءة الردود",
      voice_reply_off: "ردود صامتة",
      voice_reply_hint: "ستقرأ Sancta كل رد بصوت عالٍ.",
    },
  },
  {
    code: "fr", name: "French", nativeName: "Français",
    strings: {
      greeting_line1: "Salut {name} ! Je suis Sancta — je t'explique chaque partie de SociBaba, je t'aide à démarrer, et je réponds dans ta langue.",
      greeting_line2: "Comment puis-je t'aider ?",
      input_placeholder: "Écrire à Sancta…",
      joined_line: "Sancta a rejoint la conversation",
      active_label: "Active",
      send_aria: "Envoyer",
      suggestions_header: "Suggestions",
      thinking: "je réfléchis…",
      starter_sibascore: "Qu'est-ce que SibaScore ?",
      starter_onboard_brand: "Comment référencer ma marque ?",
      starter_aicart: "Comment fonctionne AI Cart™ ?",
      starter_pricing: "Quel plan me convient ?",
      voice_reply_on: "Lire à voix haute",
      voice_reply_off: "Réponses muettes",
      voice_reply_hint: "Sancta lira chaque réponse à voix haute.",
    },
  },
];

const STORAGE_KEY = "sb_sancta_lang";

/** Returns the current locale strings + a setter that persists to storage. */
export function useSanctaLocale() {
  const [code, setCodeState] = useState<string>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return saved;
      const browser = (typeof navigator !== "undefined" ? navigator.language.slice(0, 2) : "en").toLowerCase();
      const match = SANCTA_LANGUAGES.find(l => l.code === browser);
      return match ? browser : "en";
    } catch {
      return "en";
    }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, code); } catch {}
  }, [code]);

  const setCode = useCallback((next: string) => {
    if (SANCTA_LANGUAGES.some(l => l.code === next)) setCodeState(next);
  }, []);

  const lang = SANCTA_LANGUAGES.find(l => l.code === code) || SANCTA_LANGUAGES[0];
  const isRtl = code === "ar";

  return { code, setCode, lang, strings: lang.strings, isRtl };
}
